import { LitElement, css, html, nothing } from "lit";
import { neutral } from "../../../legl-ui/lds-colours";
import "../../../legl-ui/lds-icon";
import { spacing } from "../../../legl-ui/lds-spacing";
import "../../../legl-ui/lds-spinner";
import { typographyPresets } from "../../../legl-ui/lds-typography";
import "../../../legl-ui/tooltip";
import { PAYMENT_METHOD_CONFIG } from "../config/payment-config.js";

export class PaymentSelector extends LitElement {
  static get styles() {
    return css`
            :host {
                text-align: left;
            }

            .providers-selection h4 {
                ${typographyPresets.h4};
                font-weight: 400;
                margin: ${spacing.s} 0;
                color: ${neutral["600"]};
            }

            .providers-wrapper button {
                display: block;
                width: 100%;
                background-color: transparent;
                border: 1px solid ${neutral["200"]};
                border-bottom: none;
                text-align: left;
                padding: ${spacing.s};
                display: flex;
                align-items: center;
                justify-content: space-between;
                color: ${neutral["800"]};
            }

            .providers-wrapper button:first-child {
                border-radius: 4px 4px 0 0;
            }

            .providers-wrapper button:last-child {
                border-radius: 0 0 4px 4px;
                border-bottom: 1px solid ${neutral["200"]};
            }

            .providers-wrapper button:hover {
                cursor: pointer;
                background-color: ${neutral["100"]};
            }

            .icon-label-wrapper {
                display: flex;
                align-items: center;
            }

            .icon-label-wrapper lds-icon {
                color: ${neutral["700"]};
                margin-right: ${spacing.s};
            }

            .provider-label {
                ${typographyPresets.body};
            }

            .provider-title-wrapper {
                display: flex;
                margin-bottom: ${spacing.m};
                align-items: end;
            }

            .provider-title-wrapper button {
                border: none;
                background-color: transparent;
                ${typographyPresets.small};
                text-decoration: underline;
                color: ${neutral["700"]};
            }

            .provider-title-wrapper button:hover {
                cursor: pointer;
            }

            .provider-title {
                display: flex;
                align-items: center;
                margin-right: ${spacing.xs};
            }

            .provider-title h4 {
                ${typographyPresets.h4};
                margin: 0;
            }

            .provider-title lds-icon {
                margin-right: ${spacing.xs};
            }

            lds-spinner {
                margin-right: ${spacing.s};
            }
        `;
  }

  static get properties() {
    return {
      providers: {
        type: Array,
      },
      selectedMethod: {
        type: String,
        hasChanged(newVal, oldVal) {
          return newVal !== oldVal;
        },
      },
      isCreatingPaymentSession: {
        type: Boolean,
      },
    };
  }

  get noProviders() {
    return this.providers?.length === 0;
  }

  get hasMultipleMethods() {
    return this.filteredMethods.length > 1;
  }

  get paymentSelectedMethod() {
    if (this.selectedMethod !== null && !this.providers?.length) {
      this.dispatchEvent(
        new CustomEvent("resetProviderSelection", { bubbles: true }),
      );
    }
    if (this.selectedMethod === null && this.providers?.length === 1) {
      this.setPaymentMethod(this.filteredMethods[0]);
      return this.filteredMethods[0];
    }

    if (this.selectedMethod === null) {
      return null;
    }

    return PAYMENT_METHOD_CONFIG.filter((payment_config) => {
      return payment_config.external_provider_key === this.selectedMethod;
    })[0];
  }

  handleProviderSelection(provider) {
    this.setPaymentMethod(provider);
  }

  setPaymentMethod(provider) {
    let detail = null;

    if (provider !== null) {
      detail = {
        payment_method: provider.external_provider_key,
        provider: provider.provider,
        method: provider.method,
      };
    }

    this.dispatchEvent(
      new CustomEvent("providerSelected", {
        detail: detail,
        bubbles: true,
      }),
    );
  }

  get filteredMethods() {
    return PAYMENT_METHOD_CONFIG.filter((payment_config) => {
      const isIncluded = this.providers?.includes(
        payment_config.external_provider_key,
      );
      return isIncluded;
    });
  }

  handleBackToSelection = () => {
    this.dispatchEvent(
      new CustomEvent("resetProviderSelection", { bubbles: true }),
    );
  };

  providerHeader(provider) {
    return html`
            <div class="provider-title-wrapper">
                <div class="provider-title">
                    <lds-icon name="${provider.iconName}"></lds-icon>
                    <h4>${provider.label}</h4>
                </div>
                ${
                  this.hasMultipleMethods
                    ? html` <button
                          id="change-payment-method"
                          @click=${this.handleBackToSelection}
                      >
                          Change payment method
                      </button>`
                    : nothing
                }
            </div>
        `;
  }

  get dispayProvidersState() {
    return this.filteredMethods?.length > 1 && !this.paymentSelectedMethod;
  }

  render() {
    return html` <div>
            ${
              this.dispayProvidersState
                ? html`
                      <div class="providers-selection">
                          <h4>How would you like to pay?</h4>
                          <div class="providers-wrapper">
                              ${this.filteredMethods.map(
                                (method) => html`
                                      <button
                                          id="select-payment-method"
                                          ?disabled=${
                                            this.isCreatingPaymentSession
                                          }
                                          @click=${() =>
                                            this.handleProviderSelection(
                                              method,
                                            )}
                                      >
                                          <div class="icon-label-wrapper">
                                              ${
                                                this
                                                  .isCreatingPaymentSession ===
                                                method.method
                                                  ? html` <lds-spinner></lds-spinner>`
                                                  : html` <lds-icon
                                                        name="${method.iconName}"
                                                    ></lds-icon>`
                                              }
                                              <span class="provider-label"
                                                  >${method.label}</span
                                              >
                                          </div>
                                          ${
                                            method.tooltip
                                              ? html`
                                                    <legl-tooltip-dynamic
                                                        .hover=${true}
                                                    >
                                                        <span slot="content"
                                                            >${method.tooltip}</span
                                                        >
                                                        <lds-icon
                                                            ?small=${true}
                                                            class="tooltip-icon-info-circle"
                                                            name="lds-icon-InfoOutlined"
                                                        >
                                                        </lds-icon>
                                                    </legl-tooltip-dynamic>
                                                `
                                              : nothing
                                          }
                                      </button>
                                  `,
                              )}
                          </div>
                      </div>
                  `
                : nothing
            }
            ${
              this.noProviders
                ? html`<div class="providers-options-error">
                      No payment method available, please contact support
                  </div>`
                : nothing
            }
            ${
              this.paymentSelectedMethod
                ? html` ${this.providerHeader(this.paymentSelectedMethod)} `
                : nothing
            }
        </div>`;
  }
}

if (!customElements.get("legl-payment-selector")) {
  customElements.define("legl-payment-selector", PaymentSelector);
}
